import { inject, Injectable } from '@angular/core';
import { map, Observable, of, switchMap } from 'rxjs';
import { doSupabaseCall } from '../../shared/helpers/supabase.helper';
import type { IArchTemplateRow } from '../../shared/models';
import { AuthService } from '../auth/auth.service';
import { SupabaseService } from '../supabase/supabase';

const BUCKET = 'arch-templates';

// Template PDF di riferimento del Profilo Architettonico (es.
// "Modello_Profilo_Architettonico_Casabito.pdf"), caricato da /admin. pdf-lib lo carica e vi
// disegna sopra i contenuti generati (v. shared/helpers/arch-pdf.ts) — non più un testo di
// riferimento per il prompt AI (quell'uso è stato ritirato: il layout ora viene composto
// direttamente sul PDF, l'AI produce solo i contenuti). Storico append-only e versionato: v.
// supabase/migrations/0021_arch_template_app.sql.
@Injectable({ providedIn: 'root' })
export class ArchTemplateService {
  // --- private ---
  private readonly _supabase: SupabaseService = inject(SupabaseService);
  private readonly _auth: AuthService = inject(AuthService);

  // --- public ---
  // Ultima versione caricata; null se non è mai stato caricato nulla o Supabase non configurato.
  public getLatest(): Observable<IArchTemplateRow | null> {
    const client = this._supabase.client;
    if (null === client) {
      return of(null);
    }
    return doSupabaseCall<IRow | null>(
      client
        .from('arch_template_app')
        .select('id, version, original_name, storage_path, size, date_cre')
        .order('version', { ascending: false })
        .limit(1)
        .maybeSingle(),
    ).pipe(map((row) => (null === row ? null : this._toRow(row))));
  }

  // Bytes grezzi dell'ultimo template, per pdf-lib (PDFDocument.load). null se non c'è nessun
  // template caricato, o se il download fallisce (non deve mai bloccare la generazione: chi
  // chiama ricade sul rendering HTML generico, v. arch-pdf.ts).
  public getLatestBytes(): Observable<Uint8Array | null> {
    const client = this._supabase.client;
    if (null === client) {
      return of(null);
    }
    return this.getLatest().pipe(
      switchMap((row) => {
        if (null === row) {
          return of(null);
        }
        return doSupabaseCall<Blob>(client.storage.from(BUCKET).download(row.storagePath)).pipe(
          switchMap((blob) => blob.arrayBuffer()),
          map((buf) => new Uint8Array(buf)),
        );
      }),
    );
  }

  // Upload: prima il file nello storage (path versionato v{n}/{nome}, mai sovrascritto), SOLO se
  // riesce si scrive la riga — mai un record che punta a un file non salito (v. migrazione).
  public upload(file: File): Observable<void> {
    const client = this._supabase.client;
    const session = this._auth.sessionSignal();
    if (null === client || null === session) {
      return of(undefined);
    }
    return this.getLatest().pipe(
      switchMap((latest) => {
        const version = (latest?.version ?? 0) + 1;
        const path = `v${version}/${file.name}`;
        return doSupabaseCall(
          client.storage.from(BUCKET).upload(path, file, { contentType: file.type }),
        ).pipe(
          switchMap(() =>
            doSupabaseCall(
              client.from('arch_template_app').insert({
                version,
                original_name: file.name,
                storage_path: path,
                size: file.size,
                fk_auth_users_id: session.user.id,
              }),
            ),
          ),
          map(() => undefined),
        );
      }),
    );
  }

  private _toRow(r: IRow): IArchTemplateRow {
    return {
      id: r.id,
      version: r.version,
      originalName: r.original_name,
      storagePath: r.storage_path,
      size: r.size,
      dateCre: r.date_cre,
    };
  }
}

interface IRow {
  id: string;
  version: number;
  original_name: string;
  storage_path: string;
  size: number;
  date_cre: string;
}
